/* eslint-disable no-restricted-globals,consistent-return */
import React from 'react';
import browserHistory from 'react-router/lib/browserHistory';
import Route from 'react-router/lib/Route';
import Router from 'react-router/lib/Router';
import IndexRoute from 'react-router/lib/IndexRoute';
import IndexRedirect from 'react-router/lib/IndexRedirect';
import { Provider } from 'react-redux';
import { syncHistoryWithStore } from 'react-router-redux';
import get from 'lodash/get';
import nprogress from 'nprogress';
import * as Sentry from '@sentry/react';
import create, { runRootSaga } from '../../redux/store';

import Login from '../Auth/Login';
import SignUp from '../Auth/SignUp';
import ExpiredLink from '../Auth/ExpiredLink';
import Registration from '../Auth/Registration';
import AccessDenied from '../Auth/AccessDenied';
import ExpiredToken from '../Auth/ExpiredToken';
import CreatePassword from '../Auth/CreatePassword';
import ChangePassword from '../Auth/ChangePassword';
import RecoverPassword from '../Auth/RecoverPassword';
import AuthPersonalInfo from '../Auth/AuthPersonalInfo';
import BrowserSupportInfo from '../Auth/BrowserSupportInfo';
import ExpiredConfirmation from '../Auth/ExpiredConfirmation';
import AuthSignupInvitation from '../Auth/AuthSignupInvitation';
import MainLayout from '../MainLayout';
import NotFound from '../NotFound';
import Profile from '../ProfileLayout/Profile';
import PatientProfile from '../ProfileLayout/PatientProfile';
import ProviderProfile from '../ProfileLayout/ProviderProfile';
import ProfileSettings from '../ProfileLayout/ProfileSettings';
import ProfilePaymentMethods from '../ProfileLayout/ProfilePaymentMethods';
import Support from '../ProfileLayout/Support';
import ProfileDocumentation from '../ProfileLayout/ProfileDocumentation';
import DataSurveys from '../DataSurveys';
import PatientInfo from '../ProfileLayout/PatientInfo';
import TeamProfile from '../ProfileLayout/TeamProfile';
import ProviderInfo from '../ProfileLayout/ProviderInfo';
import Requests from './Requests';
import Community from '../Community';
import CommunityPreview from '../Community/CommunityPreview/CommunityPreview';
import DiscoverCommunities from '../Communities/DiscoverCommunities';
import MyCommunities from '../Communities/MyCommunities/index';
import OrganizationCommunities from '../Communities/OrganizationCommunities';
import Activities from '../Communities/Activities';
import { Reports } from '../Communities/Reports';
import Post from '../Post';
import CircleOfCare from '../CircleOfCare';
import PatientRegistrationInfo from '../PatientRegistration/PatientRegistrationInfo';
import PatientRegistrationContact from '../PatientRegistration/PatientRegistrationContact';
import PatientRegistrationAddress from '../PatientRegistration/PatientRegistrationAddress';
import PatientEmergencyContact from '../PatientRegistration/PatientEmergencyContact';
import Programs from '../Programs';
import Surveys from '../Surveys';
import AppAlert from '../common/AppAlert';
import LanguageProvider from '../LanguageProvider';
import AppHelmet from '../AppHelmet';
import { initializeOfflineHandling } from '../../utils/offline';
import ROUTES, {
  PAPERWORK,
  PAPERWORK_VISIT_DETAILS,
  PAPERWORK_INSURANCE_DETAILS,
  PAPERWORK_COMPLETE_VISIT_DETAILS,
  PAPERWORK_PRIMARY_CARE_PROVIDER,
  PAPERWORK_SIGN_CONSENT,
  PAPERWORK_SUBMIT,
  PAPERWORK_COMPLETE_YOUR_PROFILE,
  PAPERWORK_PREFERRED_PHARMACY,
  PAPERWORK_ADDITIONAL_INFORMATION,
  PAPERWORK_GUARANTOR_INFORMATION,
} from '../../constants/routes';
import { COMMON } from '../../constants/strings';
import {
  getPersistentState,
  initializePersistingState,
} from '../../redux/persistent-state';
import { isLoggedInSelector } from '../../redux/auth/selectors';
import {
  loggedUserIdSelector,
  loggedUserSelector,
} from '../../redux/context/selectors';
import fixVideoFullscreenScrolling from '../../utils/fixVideoFullscreenScrolling';
import { getTranslationMessages } from '../../i18n';
import { loginReferrerConsts } from '../../redux/reducers';
import * as fetchMainConsent from '../../redux/profiles/get-main-consent';

import AppPopup from '../common/AppPopup';
import { CustomToastsContainer } from '../common/CustomToastsContainer';
import { PublicProfileDetails } from '../PublicProfile/ProfileDetails';
import { PublicProfileInfo } from '../PublicProfile/ProfileInfo';
import DependentRole from '../DependentForm/DependentRole';
import DependentPersonalInfo from '../DependentForm/DependentPersonalInfo';
import DependentDocuments from '../DependentForm/DependentDocuments';
import DependentGeneralInfo from '../DependentForm/DependentGeneralInfo';
import DependentRelation from '../DependentForm/DependentRelation';
import DependentComplete from '../DependentForm/DependentComplete';
import DependentExistingUser from '../DependentForm/DependentExistingUser';
import AppointmentUsers from '../AppointmentForm/AppointmentUsers';
import AppointmentVisitType from '../AppointmentForm/AppointmentVisitType';
import AppointmentPublixInfo from '../AppointmentForm/AppointmentPublixInfo';
import AppointmentServices from '../AppointmentForm/AppointmentServices';
import AppointmentConsentForm from '../AppointmentForm/AppointmentConsentForm';
import AppointmentPatientInfo from '../AppointmentForm/AppointmentPatientInfo';
import AppointmentPayment from '../AppointmentForm/AppointmentPayment';
import AppointmentSummary from '../AppointmentForm/AppointmentSummary';
import AppointmentVisitDetails from '../AppointmentForm/AppointmentVisitDetails';
import AppointmentPharmacy from '../AppointmentForm/AppointmentPharmacy';
import HealthBoardHome from '../Healthboard/HealthboardHome';
import AppointmentListTabs from '../Healthboard/AppointmentListTabs';
import AppointmentProviderListHistory from '../Healthboard/ProviderAppointmentList/AppointmentProviderHistory';
import DashboardPatientInfo from '../DashboardRecord/DashboardPatientInfo';
import DashboardVisitInfo from '../DashboardRecord/DashboardVisitInfo';
import AppointmentProviders from '../AppointmentForm/AppointmentProviders';
import AppointmentMyProviders from '../AppointmentForm/AppointmentMyProviders';
import AppointmentProviderDetails from '../AppointmentForm/AppointmentProviderDetails';
import AppointmentHealthInformation from '../AppointmentForm/AppointmentHealthInformation';
import AppointmentHealthInformationProvider from '../AppointmentForm/AppointmentHealthInformationProvider';
import AppointmentOverview from '../AppointmentForm/AppointmentOverview';
import VisitDetails from '../VirtualVisit/VisitDetails';
import TodoListTabs from '../Healthboard/TodoListTabs';
import EmtpyHealthBoard from '../Healthboard/EmptyPage';
import VirtualVideoRoom from '../VirtualVideoRoom';
import AppointmentOrganizations from '../AppointmentForm/AppointmentOrganizations';
import { ConsentDetailsPopup } from '../Healthboard/Components/VisitConsents/ConsentDetailsPopup';
import { selectIsSignedMainConsentSelector } from '../../redux/profiles/selectors';
import { isSupportedBrowser } from '../../utils/browserChecker';
import PatientHealthboard from '../PatientHealthboard';
import { REDIRECT_TO_PAGE } from '../../constants/storageKeys';
import MembershipPaymentInfoPopup from '../Programs/MembershipPaymentInfoPopup';
import VideoCallEndPopup from '../VirtualVideoRoom/VideoCallEndPopup/VideoCallEndPopup';
import DevicePermissionsPopup from '../DevicePermissionsPopup';
import VirtualTestCallModal from '../VirtualVideoTest/VirtualTestCallModal';
import AppointmentLocations from '../AppointmentForm/AppointmentLocations';
import SignUpAzure from '../Auth/SignUpAzure';
import AppointmentAvailableTimeSlots from '../AppointmentForm/AvailableTimeSlots';
import VirtualRoom from '../VirtualRoom';
import IntercomIntegration from './IntercomIntegration';
import Paperwork from '../Paperwork';
import AppointmentPaymentInfoPopup from '../AppointmentForm/Components/AppointmentPaymentInfoPopup';

const persistentState = getPersistentState(create);
const store = create(persistentState);
initializePersistingState(store, persistentState);
initializeOfflineHandling(store);
fixVideoFullscreenScrolling();

window.ocstore = store;
runRootSaga(store);

const loggedInGuard = (currentStore, replace) => {
  const { auth, routing } = currentStore.getState();

  const loggedIn = get(auth, 'status.loggedIn');
  const pathname = get(routing, 'locationBeforeTransitions.pathname');
  const isInvitation = pathname.includes('invitations');

  if (!loggedIn && !isInvitation) {
    store.dispatch({
      type: loginReferrerConsts.SET_LOGIN_REFERRER,
      payload: {
        referrer: location.pathname,
      },
    });

    window.storage.setItem(
      REDIRECT_TO_PAGE,
      `${location.pathname}${location.search || ''}`,
    );

    return replace(ROUTES.LOGIN);
  }

  if (!isSupportedBrowser()) {
    return replace(ROUTES.BROWSER_SUPPORT);
  }
};

const rootGuard = (currentStore, replace) => {
  const state = currentStore.getState();

  if (isLoggedInSelector(state)) {
    const url =
      state.routing.loginReferrer === ROUTES.ROOT ||
      !state.routing.loginReferrer
        ? ROUTES.HEALTHBOARD
        : state.routing.loginReferrer;
    return replace(url);
  }
};

const mainConsentGuard = (currentStore, replace) => {
  const state = currentStore.getState();
  const currentUser = loggedUserSelector(state);
  const isSignedMainConsent = selectIsSignedMainConsentSelector(state);

  if (currentUser && !isSignedMainConsent) {
    store.dispatch(fetchMainConsent.actions.send());
    return replace(`/${ROUTES.HEALTHBOARD}`);
  }
};

const browserGuard = replace => {
  if (!isSupportedBrowser()) {
    return replace(`/${ROUTES.BROWSER_SUPPORT}`);
  }
};

const App = () => {
  return (
    <Provider {...{ store }}>
      <LanguageProvider messages={getTranslationMessages()}>
        <div className="App relative">
          <CustomToastsContainer />
          <AppHelmet />
          <Requests />
          <AppAlert />
          <AppPopup />
          <ConsentDetailsPopup />
          <VirtualVideoRoom />
          <VideoCallEndPopup />
          <DevicePermissionsPopup />
          <VirtualTestCallModal />
          <MembershipPaymentInfoPopup />
          <AppointmentPaymentInfoPopup />
          <IntercomIntegration />

          <Router history={syncHistoryWithStore(browserHistory, store)}>
            <Route
              path={ROUTES.VIRTUAL_ROOM}
              getComponent={(nextState, cb) => {
                require.ensure([], () => {
                  nprogress.start();
                  const VirtualCall = () => <VirtualRoom />;
                  cb(null, VirtualCall);
                  nprogress.done();
                });
              }}
            />
            <Route
              path={ROUTES.IN_OFFICE_VISIT}
              getComponent={(nextState, cb) => {
                require.ensure([], require => {
                  nprogress.start();
                  const InOfficeVirtualVisit = require('./../InOfficeVirtualVisit/index')
                    .default;
                  cb(null, InOfficeVirtualVisit);
                  nprogress.done();
                });
              }}
            />
            <Route
              path={ROUTES.CONNECTION_ROOM}
              getComponent={(nextState, cb) => {
                require.ensure([], () => {
                  nprogress.start();
                  const VirtualCall = () => <VirtualRoom connectionRoom />;
                  cb(null, VirtualCall);
                  nprogress.done();
                });
              }}
            />

            <Route path={`${PAPERWORK}/:uuid`} component={Paperwork}>
              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_VISIT_DETAILS}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const PaperworkVisitDetails = require('../Paperwork/pages/PaperworkVisitDetails')
                      .default;
                    cb(null, PaperworkVisitDetails);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_INSURANCE_DETAILS}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const PaperworkInsuranceDetails = require('../Paperwork/pages/InsuranceDetails')
                      .default;
                    cb(null, PaperworkInsuranceDetails);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_COMPLETE_YOUR_PROFILE}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const CompleteYourProfile = require('../Paperwork/pages/CompleteYourProfile')
                      .default;
                    cb(null, CompleteYourProfile);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_GUARANTOR_INFORMATION}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const GuarantorInformationDetails = require('../Paperwork/pages/GuarantorInformationDetails')
                      .default;
                    cb(null, GuarantorInformationDetails);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_PREFERRED_PHARMACY}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const PreferredPharmacy = require('../Paperwork/pages/PreferredPharmacy')
                      .default;
                    cb(null, PreferredPharmacy);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_PRIMARY_CARE_PROVIDER}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const PaperworkPrimaryCareProvider = require('../Paperwork/pages/PrimaryCareProvider')
                      .default;
                    cb(null, PaperworkPrimaryCareProvider);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_ADDITIONAL_INFORMATION}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const AdditionalInformation = require('../Paperwork/pages/AdditionalInformation')
                      .default;
                    cb(null, AdditionalInformation);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_SIGN_CONSENT}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const PaperworkSignConsent = require('../Paperwork/pages/SignConsent')
                      .default;
                    cb(null, PaperworkSignConsent);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_SUBMIT}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const PaperworkSubmit = require('../Paperwork/pages/Submit')
                      .default;
                    cb(null, PaperworkSubmit);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${PAPERWORK}/:uuid/${PAPERWORK_COMPLETE_VISIT_DETAILS}`}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const PaperworkCompletedVisitDetails = require('../Paperwork/pages/PaperworkCompletedVisitDetails')
                      .default;
                    cb(null, PaperworkCompletedVisitDetails);
                    nprogress.done();
                  });
                }}
              />
            </Route>

            <Route path={ROUTES.ROOT} component={MainLayout}>
              <IndexRoute
                onEnter={(nextState, replace) => {
                  loggedInGuard(store, replace);
                  rootGuard(store, replace);
                }}
                component={() => (
                  <div style={{ fontSize: '5em', padding: '1em' }}>
                    Root page
                  </div>
                )}
              />

              <Route
                path={ROUTES.ROOT}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const Auth = require('./../Auth/index').default;
                    cb(null, Auth);
                    nprogress.done();
                  });
                }}
              >
                <Route
                  path={ROUTES.BROWSER_SUPPORT}
                  components={{ children: BrowserSupportInfo }}
                />
              </Route>
              <Route
                path={ROUTES.ROOT}
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    nprogress.start();
                    const Auth = require('./../Auth/index').default;
                    cb(null, Auth);
                    nprogress.done();
                  });
                }}
              >
                <Route
                  path={ROUTES.ACCESS_DENIED}
                  components={{ children: AccessDenied }}
                />
                <Route
                  path={ROUTES.ACCESS_DENIED_COMMUNITIES}
                  getComponent={(nextState, cb) => {
                    require.ensure([], () => {
                      const forwardRoute = {
                        name: COMMON.myCommunities,
                        path: ROUTES.COMMUNITIES,
                      };
                      const ErrorScreen = () => (
                        <AccessDenied {...{ forwardRoute }} />
                      );
                      cb(null, ErrorScreen);
                    });
                  }}
                />
                <Route
                  path={ROUTES.EXPIRED_TOKEN}
                  components={{ children: ExpiredToken }}
                />
                <Route
                  path={ROUTES.EXPIRED_LINK}
                  components={{ children: ExpiredLink }}
                />
                <Route
                  path={ROUTES.DEFAULT_EXPIRED_LINK}
                  components={{ children: ExpiredLink }}
                />
                <Route
                  path={ROUTES.CONFIRMATION}
                  components={{ children: ExpiredConfirmation }}
                />
                <Route path={ROUTES.LOGIN} components={{ children: Login }} />
                <Route
                  path={ROUTES.PERSONAL_INFO}
                  components={{ children: AuthPersonalInfo }}
                />
                <Route
                  path={ROUTES.RESET_PASSWORD}
                  components={{ children: RecoverPassword }}
                />
                <Route
                  path={ROUTES.CHANGE_PASSWORD}
                  components={{ children: ChangePassword }}
                />
                <Route
                  path={ROUTES.CREATE_PASSWORD}
                  components={{ children: CreatePassword }}
                />
                <Route path={ROUTES.SIGNUP} components={{ children: SignUp }} />
                <Route
                  path={ROUTES.SIGN_UP_AZURE}
                  components={{ children: SignUpAzure }}
                />
                <Route
                  path={ROUTES.ACCEPT_INVITATION}
                  components={{ children: Registration }}
                />
                <Route
                  path={ROUTES.SIGNUP_INVITATION}
                  components={{ children: AuthSignupInvitation }}
                />
              </Route>
              <Route
                onEnter={(nextState, replace) => {
                  browserGuard(replace);
                }}
                path={`${ROUTES.COMMUNITY_PREVIEW}/:communityId`}
                components={{ children: CommunityPreview }}
              />
              <Route
                path={ROUTES.MESSAGES}
                onEnter={(nextState, replace) => {
                  loggedInGuard(store, replace);
                  mainConsentGuard(store, replace);
                }}
                getComponent={(nextState, cb) => {
                  nprogress.start();

                  require.ensure([], require => {
                    const Messages = require('./../Messages/index').default;
                    cb(null, Messages);
                    nprogress.done();
                  });
                }}
              />
              <Route
                path={`${ROUTES.CIRCLE_OF_CARE}/:id`}
                components={{ children: CircleOfCare }}
              />
              <Route
                path={`${ROUTES.PROGRAM}/:id`}
                components={{ children: Programs }}
              />
              <Route
                path={ROUTES.PATIENT_REGISTRATION}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const PatientRegistration_ = require('../PatientRegistration')
                      .default;
                    cb(null, PatientRegistration_);
                    nprogress.done();
                  });
                }}
              >
                <IndexRedirect to={ROUTES.CONTACT_INFO} />
                <Route
                  path={ROUTES.CONTACT_INFO}
                  components={{ children: PatientRegistrationContact }}
                />
                <Route
                  path={ROUTES.PERSONAL_INFO}
                  components={{ children: PatientRegistrationInfo }}
                />
                <Route
                  path={ROUTES.ADDRESS}
                  components={{ children: PatientRegistrationAddress }}
                />
                <Route
                  path={ROUTES.EMERGENCY_CONTACT}
                  components={{ children: PatientEmergencyContact }}
                />
              </Route>

              <Route
                path={`${ROUTES.SURVEYS_GENERAL_SUB_PATH}/:contextId`}
                components={{ children: Surveys }}
              />
              <Route
                path={ROUTES.CIRCLES}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();

                  require.ensure([], require => {
                    const CirclesLayout = require('./../CirclesLayout/index')
                      .default;
                    cb(null, CirclesLayout);
                    nprogress.done();
                  });
                }}
              />
              <Route
                path={ROUTES.APP_NOTIFICATIONS}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();

                  require.ensure([], require => {
                    const DependentManagement = require('./../DependentManagement/index')
                      .default;
                    cb(null, DependentManagement);
                    nprogress.done();
                  });
                }}
              />
              <Route
                path={ROUTES.DASHBOARD}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const Component = require('./../Dashboard/index').default;
                    cb(null, Component);
                    nprogress.done();
                  });
                }}
              />
              <Route
                path={ROUTES.COMMUNITIES}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();

                  require.ensure([], require => {
                    const Communities = require('./../Communities/index')
                      .default;
                    cb(null, Communities);
                    nprogress.done();
                  });
                }}
              >
                <IndexRedirect to={ROUTES.DISCOVER} />
                <Route
                  path={`${ROUTES.DISCOVER}/${ROUTES.ORGANIZATION}`}
                  components={{ children: OrganizationCommunities }}
                />
                <Route
                  path={ROUTES.DISCOVER}
                  components={{ children: DiscoverCommunities }}
                />
                <Route
                  path={ROUTES.MY_COMMUNITIES}
                  components={{ children: MyCommunities }}
                />
                <Route
                  path={ROUTES.ACTIVITIES}
                  components={{ children: Activities }}
                />
                <Route
                  path={ROUTES.REPORTS}
                  components={{ children: Reports }}
                />
                <Route
                  path=":communityId"
                  components={{ children: Community }}
                />
                <Route
                  path={`:communityId/${ROUTES.NOT_FOUND}`}
                  component={NotFound}
                />
                <Route
                  path={`:communityId/${ROUTES.POST}/:postId`}
                  components={{ children: Post }}
                />
              </Route>
              <Route
                path={ROUTES.PUBLIC_PROFILE}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const PublicProfile = require('./../PublicProfile').default;
                    cb(null, PublicProfile);
                    nprogress.done();
                  });
                }}
              >
                <Route
                  path=":id"
                  components={{ children: PublicProfileDetails }}
                />
                <Route
                  path={ROUTES.PUBLIC_PROFILE_INFO}
                  components={{ children: PublicProfileInfo }}
                />
              </Route>

              <Route
                path={ROUTES.PATIENT_HEALTHBOARD}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const Component = require('./../PatientHealthboard')
                      .default;
                    cb(null, Component);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={ROUTES.HEALTHBOARD}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const Component = require('./../Healthboard').default;
                    cb(null, Component);
                    nprogress.done();
                  });
                }}
              >
                <IndexRoute
                  onEnter={(nextState, replace) =>
                    replace(
                      `${ROUTES.HEALTHBOARD}/${loggedUserIdSelector(
                        store.getState(),
                      )}/${ROUTES.HEALTHBOARD_HOME}`,
                    )
                  }
                />

                <Route
                  path={`:id/${ROUTES.HEALTHBOARD_HOME}`}
                  components={{ children: HealthBoardHome }}
                />

                <Route
                  path={`:id/${ROUTES.HEALTHBOARD_TODO}`}
                  components={{ children: TodoListTabs }}
                />
                <Route
                  path={`:id/${ROUTES.HEALTHBOARD_APPOINTMENTS}`}
                  components={{ children: AppointmentListTabs }}
                />
                <Route
                  path={`:id/${ROUTES.HEALTHBOARD_APPOINTMENTS_HISTORY}`}
                  components={{ children: AppointmentProviderListHistory }}
                />
              </Route>

              <Route
                path={ROUTES.HEALTHBOARD_EMPTY}
                components={{ children: EmtpyHealthBoard }}
              />

              <Route
                path={ROUTES.DEPENDENT_FORM}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const DependentForm = require('./../DependentForm').default;
                    cb(null, DependentForm);
                    nprogress.done();
                  });
                }}
              >
                <IndexRedirect to={ROUTES.DEPENDENT_FORM_ROLE} />

                <Route
                  path={ROUTES.DEPENDENT_FORM_ROLE}
                  components={{ children: DependentRole }}
                />
                <Route
                  path={ROUTES.DEPENDENT_FORM_PERSONAL}
                  components={{ children: DependentPersonalInfo }}
                />
                <Route
                  path={ROUTES.DEPENDENT_FORM_GENERAL}
                  components={{ children: DependentGeneralInfo }}
                />
                <Route
                  path={ROUTES.DEPENDENT_FORM_DOCUMENTS}
                  components={{ children: DependentDocuments }}
                />
                <Route
                  path={ROUTES.DEPENDENT_FORM_RELATION}
                  components={{ children: DependentRelation }}
                />
                <Route
                  path={ROUTES.DEPENDENT_FORM_COMPLETE}
                  components={{ children: DependentComplete }}
                />
                <Route
                  path={ROUTES.DEPENDENT_FORM_EXISTING}
                  components={{ children: DependentExistingUser }}
                />
              </Route>

              <Route
                path={ROUTES.APPOINTMENT_FORM}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const Root = require('./../AppointmentForm').default;
                    cb(null, Root);
                    nprogress.done();
                  });
                }}
              >
                <IndexRedirect to={ROUTES.APPOINTMENT_FORM_USERS} />

                <Route
                  path={ROUTES.APPOINTMENT_FORM_USERS}
                  components={{ children: AppointmentUsers }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_ORGANIZATIONS}
                  components={{ children: AppointmentOrganizations }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_VISIT_TYPE}
                  components={{ children: AppointmentVisitType }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_PUBLIX_INFO}
                  components={{ children: AppointmentPublixInfo }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_VISIT_SERVICES}
                  components={{ children: AppointmentServices }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_VISIT_LOCATIONS}
                  components={{ children: AppointmentLocations }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_CONSENT_FORM}
                  components={{ children: AppointmentConsentForm }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_PHARMACY}
                  components={{ children: AppointmentPharmacy }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_HEALTH_INFO}
                  components={{ children: AppointmentHealthInformation }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_HEALTH_INFO_PROVIDER}
                  components={{
                    children: AppointmentHealthInformationProvider,
                  }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_PAYMENT}
                  components={{ children: AppointmentPayment }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_PATIENT_INFO}
                  components={{ children: AppointmentPatientInfo }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_VISIT_DETAILS}
                  components={{ children: AppointmentVisitDetails }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_EMERGENCY_CONTACT}
                  getComponent={(nextState, cb) => {
                    require.ensure([], require => {
                      const AppointmentEmergencyContact = require('../AppointmentForm/AppointmentEmergencyContact')
                        .default;
                      cb(null, AppointmentEmergencyContact);
                    });
                  }}
                />

                <Route
                  path={ROUTES.APPOINTMENT_FORM_GUARANTOR_INFORMATION}
                  getComponent={(nextState, cb) => {
                    require.ensure([], require => {
                      const AppointmentGuarantorInformation = require('../AppointmentForm/AppointmentGuarantorInformation')
                        .default;
                      cb(null, AppointmentGuarantorInformation);
                    });
                  }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_ADDITIONAL_INFO}
                  getComponent={(nextState, cb) => {
                    require.ensure([], require => {
                      const AppointmentAdditionalInfo = require('../AppointmentForm/AppointmentAdditionalInfo')
                        .default;
                      cb(null, AppointmentAdditionalInfo);
                    });
                  }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_PROVIDERS}
                  components={{ children: AppointmentProviders }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_MY_PROVIDERS}
                  getComponent={(nextState, cb) => {
                    require.ensure([], () => {
                      nprogress.start();
                      const MyRescheduleProviders = () => (
                        <AppointmentMyProviders fullView />
                      );
                      cb(null, MyRescheduleProviders);
                      nprogress.done();
                    });
                  }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_AVAILABLE_TIME_SLOTS}
                  components={{ children: AppointmentAvailableTimeSlots }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_PROVIDER_DETAILS}
                  components={{ children: AppointmentProviderDetails }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_OVERVIEW}
                  components={{ children: AppointmentOverview }}
                />
                <Route
                  path={ROUTES.APPOINTMENT_FORM_SUMMARY}
                  components={{ children: AppointmentSummary }}
                />

                <Route
                  path={ROUTES.APPOINTMENT_FORM_INSURANCE_FORM}
                  getComponent={(nextState, cb) => {
                    nprogress.start();
                    require.ensure([], require => {
                      const AppointmentInsuranceFullForm = require('../AppointmentForm/AppointmentInsuranceFullForm')
                        .default;
                      cb(null, AppointmentInsuranceFullForm);
                      nprogress.done();
                    });
                  }}
                />
              </Route>

              <Route
                path={ROUTES.DASHBOARD_RECORD}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const DashboardRecord = require('./../DashboardRecord')
                      .default;
                    cb(null, DashboardRecord);
                    nprogress.done();
                  });
                }}
              >
                <IndexRedirect to={ROUTES.PATIENT_INFO} />
                <Route
                  path={ROUTES.PATIENT_INFO}
                  components={{ children: DashboardPatientInfo }}
                />
                <Route
                  path={ROUTES.DASHBOARD_VISIT_INFO}
                  components={{ children: DashboardVisitInfo }}
                />
              </Route>

              <Route
                path={ROUTES.CHOOSE_ADD_ACCOUNT}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const DashboardRecord = require('./../ProfileLayout/ChooseAddAccount')
                      .default;
                    cb(null, DashboardRecord);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={ROUTES.VIRTUAL_VISIT}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const VirtualVisit = require('./../VirtualVisit').default;
                    cb(null, VirtualVisit);
                    nprogress.done();
                  });
                }}
              >
                <Route
                  path={`:userId/${ROUTES.DETAILS}`}
                  components={{ children: VisitDetails }}
                />
              </Route>

              <Route
                path={ROUTES.PROFILE}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const ProfileLayout = require('./../ProfileLayout').default;
                    cb(null, ProfileLayout);
                    nprogress.done();
                  });
                }}
              >
                <Route
                  path={`:id/${ROUTES.CIRCLE_OF_CARE}`}
                  components={{ children: CircleOfCare }}
                />
                <Route
                  path={`:id/${ROUTES.SURVEYS_GENERAL_SUB_PATH}`}
                  components={{ children: DataSurveys }}
                />
                <Route
                  path={`:id/${ROUTES.PROGRAM}`}
                  components={{ children: Programs }}
                />
                <Route
                  path={`:id/${ROUTES.PROFILE_DOCUMENTATION}`}
                  components={{ children: ProfileDocumentation }}
                />
                <Route
                  path={`:id/${ROUTES.PROFILE_SETTINGS}`}
                  components={{ children: ProfileSettings }}
                />
                <Route
                  path={`:id/${ROUTES.PROFILE_PAYMENT_METHODS}`}
                  components={{ children: ProfilePaymentMethods }}
                />
                <Route
                  path={`:id/${ROUTES.SUPPORT}`}
                  components={{ children: Support }}
                />
                <Route
                  path={`:id/${ROUTES.PATIENT_INFO}`}
                  components={{ children: PatientInfo }}
                />
                <Route
                  path={`:id/${ROUTES.PROVIDER_INFO}`}
                  components={{ children: ProviderInfo }}
                />
                <Route
                  path={`:id/${ROUTES.PATIENT_HEALTHBOARD}`}
                  components={{ children: PatientHealthboard }}
                />
                {/* additional route for provider profile info (from user Circle of
              care page) */}
                <Route
                  path={`:id/${ROUTES.PROVIDER_PROFILE}/:memberId/${ROUTES.PROVIDER_INFO}`}
                  components={{ children: ProviderInfo }}
                />
                <Route
                  path={`:id/${ROUTES.PROVIDER_MEMBER_PROFILE}/:memberId/${ROUTES.PROVIDER_INFO}`}
                  components={{ children: ProviderInfo }}
                />
                <Route
                  path={`:id/${ROUTES.PARENT_PROVIDER}/:memberId/${ROUTES.PROVIDER_INFO}`}
                  components={{ children: ProviderInfo }}
                />
                <Route path=":id" components={{ children: Profile }}>
                  <Route
                    path={ROUTES.PATIENT_PROFILE}
                    components={{ children: PatientProfile }}
                  />
                  <Route
                    path={ROUTES.PROVIDER_PROFILE}
                    components={{ children: ProviderProfile }}
                  />
                  <Route
                    path={ROUTES.USER_PROFILE}
                    components={{ children: PatientProfile }}
                  />
                  <Route
                    path={`${ROUTES.PATIENT_PROFILE}-view`}
                    components={{ children: PatientProfile }}
                  />
                  <Route
                    path={`${ROUTES.FAMILY_MEMBER_PROFILE}/:memberId`}
                    components={{ children: PatientProfile }}
                  />
                  <Route
                    path={`${ROUTES.PROVIDER_PROFILE}/:memberId`}
                    components={{ children: ProviderProfile }}
                  />
                  <Route
                    path={`${ROUTES.PROVIDER_MEMBER_PROFILE}/:memberId`}
                    components={{ children: ProviderProfile }}
                  />
                  <Route
                    path={`${ROUTES.TEAM_MEMBER_PROFILE}/:memberId`}
                    components={{ children: TeamProfile }}
                  />
                  <Route
                    path={`${ROUTES.PARENT_PROVIDER}/:memberId`}
                    components={{ children: ProviderProfile }}
                  />
                </Route>
              </Route>

              <Route
                path={ROUTES.PROVIDER_CALENDAR}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const Calendar = require('./../ProviderCalendar').default;
                    cb(null, Calendar);
                    nprogress.done();
                  });
                }}
              />
              <Route
                path="/:id/:providerId/test"
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const Calendar = require('./../ProviderCalendar/Components/test')
                      .default;
                    cb(null, Calendar);
                    nprogress.done();
                  });
                }}
              />
              <Route
                path={ROUTES.CALENDAR_MOB}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const MobileView = require('./../ProviderCalendar/MobileView')
                      .default;
                    cb(null, MobileView);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path={`${ROUTES.APPOINTMENTS}/:id`}
                onEnter={(nextState, replace) => loggedInGuard(store, replace)}
                getComponent={(nextState, cb) => {
                  nprogress.start();
                  require.ensure([], require => {
                    const Appointments = require('./../Appointments').default;
                    cb(null, Appointments);
                    nprogress.done();
                  });
                }}
              />

              <Route
                path="styleguide"
                getComponent={(nextState, cb) => {
                  require.ensure([], require => {
                    const StyleGuide = require('./StyleGuide').default;
                    cb(null, StyleGuide);
                  });
                }}
              />
              <Route path="*" component={NotFound} />
            </Route>
          </Router>
        </div>
      </LanguageProvider>
    </Provider>
  );
};

export default Sentry.withProfiler(App);



// WEBPACK FOOTER //
// ./src/components/App/index.jsx